/**
 * CRM Lead Inbox / Patient database
 *
 * Two modes of the same page:
 *  - inbox (default, /crm): recruiter triage inbox for the lead pipeline with
 *    a List | Board view toggle (persisted via getPref/setPref). Board view
 *    renders a per-stage KanbanBoard fed by the current filters; list view is
 *    the classic table/cards with pipeline tabs and bulk actions.
 *  - database (/crm/database): the all-patients directory — same search,
 *    filter panel, sort, bulk bar, and table/cards, but no pipeline tabs,
 *    tasks panel, view toggle, or test tools. Archived rows get a gray chip
 *    in both modes.
 *
 * Pipeline tabs are driven by GET /contacts/stats; the list maps 1:1 to
 * GET /contacts params (q, lifecycle, eligibility, study, owner, unassigned,
 * sort, and `list` — a Search-tab run's frozen member list, the board bridge).
 * The old form/answers + condition/medication/lab cohort filters moved to the
 * dedicated Search tab (/crm/search). All filters auto-apply (text
 * inputs debounced 300ms). Rows
 * show study chips, status, inline owner assignment, and the last
 * communication (with a needs-your-reply indicator), and open the contact
 * detail (/crm/contacts/:id). Bulk assign / set-status PATCHes each selected
 * contact sequentially. A MyTasksPanel above the tabs surfaces the recruiter's
 * due-today/overdue follow-ups. Super-admin keeps the Phase 2 test tools
 * (seed flows / reconciliation / automation tick) in a menu.
 */
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  ChevronDown,
  FlaskConical,
  Inbox,
  LayoutGrid,
  List,
  Loader2,
  MoonStar,
  RefreshCw,
  Send,
  UserPlus,
  Users,
  Workflow,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { ContactListItem, InterestCategory, LifecycleStats, SavedSearch, StudyOption, contactName, studyInternalLabel } from '../types';
import { CONSIDERING_LIFECYCLES, INCOMING_ELIGIBILITY_OPTIONS, FINAL_DISPOSITIONS, LIFECYCLES, MILESTONE_FILTER_OPTIONS, MILESTONE_LABELS, STUDY_ATTRIBUTED_STAGES, dateTime, formatPhone, getPref, needsReply, notifyStatsChanged, prettySlug, relTime, setPref, useIsPhone, useTeam } from '../lib';
import { ActiveFilters, AddFilterMenu, FilterBar, FilterChip, FilterMultiSelect, FilterSelect, LifecycleBadge, MergeCandidatesModal, MilestoneChip, MyTasksPanel, OwnerSelect, PageHeader, RefreshButton, SearchInput, SegmentedControl, SegmentedTabs, SnoozeButton, isActiveSnooze } from '../components';
import type { ActiveChip, FilterDef, MergeCandidate } from '../components';
import { ChannelIcon, StudyChips, RowLink, useRowNav } from '../components/shared';
import KanbanBoard from '../components/KanbanBoard';
import { Alert, Z } from '../components/ui';
import { useWindowedSlice } from '../useWindowedSlice';
import NewLeadModal from '../components/NewLeadModal';
import DispositionReasonModal from '../components/DispositionReasonModal';
import StageStudyConfirmModal from '../components/StageStudyConfirmModal';
import RandomizedConflictModal from '../components/RandomizedConflictModal';

const LIMIT = 50;
// Sentinel for the bulk-assign "Unassigned" option (assign owner -> null).
const UNASSIGNED = '__unassigned__';

/**
 * What one bulk PATCH /contacts/:id carries. Named (it used to be inline) so the
 * randomized-guard retry can stash the exact body it was blocked on and re-send
 * it unchanged — a re-send that quietly differed would move leads somewhere the
 * recruiter never confirmed.
 */
type BulkPatchBody =
  | { owner_user_id: string | null }
  | { lifecycle_status: string; reason_code?: string; disposition_note?: string; study_confirmed?: boolean };

interface Filters {
  q: string;
  /** Pipeline stage(s) — empty = all. Sent comma-joined; backend ORs them. */
  lifecycle: string[];
  /** Incoming (landing-form) eligibility verdict(s) — empty = any. */
  eligibility: string[];
  /** Derived pertinent-milestone key(s) — empty = any. */
  milestone: string[];
  /** Effective study slug(s) — empty = any. */
  study: string[];
  /** Owner user id(s) — empty = any. Combines with `unassigned` as an OR. */
  owner: string[];
  unassigned: boolean;
  /** Inbox list only: flip the list to the archived-only set (conversation=archived). */
  showArchived: boolean;
  /** Inbox/board only: reveal leads snoozed to a future time (hidden by default). */
  showSnoozed: boolean;
  sort: string; // '' = newest (default), 'oldest', 'activity' (last_activity_at desc), 'eligibility' (incoming verdict)
  /**
   * Search-tab bridge: narrow to a search run's member list (a crm.search_runs
   * id, '' = off). listName is display-only (the chip / select label).
   * The old medical/answers cohort filters moved to the Search tab (/crm/search).
   */
  list: string;
  listName: string;
  /**
   * Future-study interest list scope (crm.interest_categories id or key,
   * '' = off). interestName is display-only (the chip label).
   */
  interest: string;
  interestName: string;
}

const DEFAULT_FILTERS: Filters = {
  q: '',
  lifecycle: [],
  eligibility: [],
  milestone: [],
  study: [],
  owner: [],
  unassigned: false,
  showArchived: false,
  showSnoozed: false,
  sort: '',
  list: '',
  listName: '',
  interest: '',
  interestName: '',
};

// Coerce a persisted/seeded multi-select value into a clean string[]. Accepts
// the new array shape, an OLD scalar ("atlas-2" -> ["atlas-2"]; "" -> []), or
// junk (-> []). Shared by loadFilters + consumeUrlFilterSeed.
const toArr = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && x !== '')
  : typeof v === 'string' && v !== '' ? [v]
  : [];

// localStorage pref key (namespaced under `crm.` by getPref/setPref). Persisted
// per mode so the inbox and the patient database keep their own filter sets —
// switching between them, or navigating away and back, restores each one.
const FILTERS_PREF = 'contacts.filters';

/**
 * Consume deep-link filter params (the Search run page's "Open in board" jump:
 * ?list=<run id>&list_name=…). Recognized params override the persisted filter
 * set and are stripped from the URL so refresh/back doesn't re-apply them.
 * Returns null when the URL carries none. (The retired ?condition=/?lab= deep
 * links now land in the Search builder instead — see ProfileKeysLibrary.)
 */
function consumeUrlFilterSeed(): Partial<Filters> | null {
  const sp = new URLSearchParams(window.location.search);
  const list = sp.get('list');
  const interest = sp.get('interest');
  if (!list && !interest) return null;
  const seed: Partial<Filters> = {};
  if (list) {
    seed.list = list;
    seed.listName = sp.get('list_name') || 'Search list';
  }
  // Future-study interest list jump (the Settings page's "View leads" link).
  if (interest) {
    seed.interest = interest;
    seed.interestName = sp.get('interest_name') || interest;
  }
  ['list', 'list_name', 'interest', 'interest_name'].forEach((k) => sp.delete(k));
  const rest = sp.toString();
  window.history.replaceState(null, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash || ''}`);
  return seed;
}

/**
 * Load this mode's persisted filters, merged over the defaults. Built from
 * EXPLICIT field picks so retired persisted keys (the old form/answers +
 * condition/medication/lab filter state) are dropped on read instead of
 * carried forever.
 */
function loadFilters(mode: 'inbox' | 'database'): Filters {
  const raw = getPref(`${FILTERS_PREF}.${mode}`, '');
  if (!raw) return DEFAULT_FILTERS;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      ...DEFAULT_FILTERS,
      q: typeof parsed.q === 'string' ? parsed.q : '',
      // Multi-select migration: an OLD persisted scalar ("atlas-2") becomes
      // ["atlas-2"], "" becomes [] — coerce so a legacy pref keeps working.
      lifecycle: toArr(parsed.lifecycle),
      eligibility: toArr(parsed.eligibility),
      milestone: toArr(parsed.milestone),
      study: toArr(parsed.study),
      owner: toArr(parsed.owner),
      unassigned: parsed.unassigned === true,
      showArchived: parsed.showArchived === true,
      showSnoozed: parsed.showSnoozed === true,
      sort: typeof parsed.sort === 'string' ? parsed.sort : '',
      list: typeof parsed.list === 'string' ? parsed.list : '',
      listName: typeof parsed.listName === 'string' ? parsed.listName : '',
      interest: typeof parsed.interest === 'string' ? parsed.interest : '',
      interestName: typeof parsed.interestName === 'string' ? parsed.interestName : '',
    };
  } catch {
    return DEFAULT_FILTERS;
  }
}

function buildParams(f: Filters, offset: number, hideArchived: boolean): Record<string, string | number> {
  const p: Record<string, string | number> = { limit: LIMIT, offset };
  // Archived-only view (inbox chip): the backend ignores hide_archived for this
  // tab. Otherwise the leads inbox hides archived leads; the patient database
  // (hideArchived === false) shows everyone.
  if (f.showArchived) {
    p.conversation = 'archived';
  } else if (hideArchived) {
    p.hide_archived = 'true';
  }
  // The inbox/board hide future-snoozed leads unless "Show snoozed" is on; the
  // patient database (hideArchived === false) always lists everyone.
  p.show_snoozed = hideArchived && !f.showSnoozed ? 'false' : 'true';
  if (f.q.trim()) p.q = f.q.trim();
  // Multi-select filters: comma-join the selected values; omit entirely when
  // empty (empty === "any", so the backend applies no constraint).
  if (f.lifecycle.length) p.lifecycle = f.lifecycle.join(',');
  if (f.eligibility.length) p.eligibility = f.eligibility.join(',');
  if (f.milestone.length) p.milestone = f.milestone.join(',');
  if (f.study.length) p.study = f.study.join(',');
  if (f.owner.length) p.owner = f.owner.join(',');
  // "Mine" keeps a lead whose main recruiter is now someone else but who is
  // still mine on another open study (a returning lead handed to the new
  // study's recruiter; migration 20260929190000). The server ignores it under a
  // study scope, where the owner is that study's recruiter anyway.
  if (f.owner.length) p.include_study_rows = 'true';
  if (f.unassigned) p.unassigned = 'true';
  if (f.sort) p.sort = f.sort;
  // Search-run list scope (the Search tab's board bridge).
  if (f.list) p.list = f.list;
  // Future-study interest list scope.
  if (f.interest) p.interest = f.interest;
  return p;
}

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

/** Shared by the header "select page" box and every row's box. */
const checkboxCls =
  'h-4 w-4 cursor-pointer rounded border-gray-300 text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700';

/** First (earliest) lead source for a row's subline. */
const sourceOf = (c: ContactListItem): string | null => {
  const src = (c.studies || []).find((s) => s.lead_source)?.lead_source;
  return src ? prettySlug(src) : null;
};

/**
 * Most recent lead arrival = the max first_seen_at across the contact's leads,
 * or null when it has none. The backend sorts studies ascending by
 * first_seen_at, so the LAST is the most recent submission; the max is computed
 * defensively in case that order ever changes. Intentionally the max (not the
 * earliest) so a re-submitted lead — which inserts a new contact_leads row —
 * shows its new arrival time, per the owner clarification.
 */
const arrivalOf = (c: ContactListItem): string | null => {
  const s = c.studies;
  if (!s || s.length === 0) return null;
  return s.reduce<string | null>(
    (max, x) => (x.first_seen_at && (!max || x.first_seen_at > max) ? x.first_seen_at : max),
    null,
  );
};

/**
 * "Also with Ronni D." under a lead's recruiter: the recruiters who have them on
 * ANOTHER open study (a lead on two studies can have two; migration
 * 20260929190000). Nothing for a one-recruiter lead.
 */
function OtherRecruitersNote({ c, className = '' }: { c: ContactListItem; className?: string }) {
  const { byId } = useTeam();
  const others = [...new Set((c.memberships || [])
    .filter((m) => m.owner_user_id && m.owner_user_id !== c.owner_user_id
      && CONSIDERING_LIFECYCLES.includes(m.status))
    .map((m) => m.owner_user_id as string))];
  if (others.length === 0) return null;
  const names = others.map((id) => byId.get(id)?.name || 'another recruiter');
  return (
    <div
      className={`text-xs text-gray-500 dark:text-gray-400 ${className}`}
      title="Also being considered for another study, with its own recruiter (see the lead's Studies card)"
    >
      Also with {names.join(', ')}
    </div>
  );
}

/**
 * One lead in the desktop table.
 *
 * Extracted and memoized (D-9): a 150-row list re-rendered every row on every
 * keystroke, every selection tick and every owner assignment. Memo only pays if
 * the props are shallow-stable, so selection arrives as the BOOLEAN
 * `isSelected` (never the `selected` Set, whose identity changes on every
 * toggle) and every handler is one of the page's useCallback'd row handlers.
 * The markup is byte-for-byte the inline <tr> it replaces.
 */
const ContactRow = memo(function ContactRow({
  c, isSelected, isDatabase, showSnoozeUi, rowNav, onOpen, onToggleSelect, onOwnerChanged, onArchive, onSnooze,
}: {
  c: ContactListItem;
  isSelected: boolean;
  isDatabase: boolean;
  /** Inbox, not-the-archived-view: the snooze chip + button belong to working views. */
  showSnoozeUi: boolean;
  rowNav: (to: string) => { onClick: (e: React.MouseEvent) => void; onAuxClick: (e: React.MouseEvent) => void };
  onOpen: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onOwnerChanged: (id: string, owner: string | null) => void;
  onArchive: (row: ContactListItem, archive: boolean) => void;
  onSnooze: (row: ContactListItem, untilIso: string | null) => void;
}) {
  const reply = needsReply(c.last_comm);
  const contactInfo = [c.phone_e164 ? formatPhone(c.phone_e164) : null, c.email].filter(Boolean).join(' · ');
  const source = sourceOf(c);
  return (
    <tr
      tabIndex={0}
      {...rowNav(`/crm/contacts/${c.id}`)}
      onKeyDown={(e) => {
        // Only when the row itself is focused — Enter on an
        // embedded checkbox/select must not navigate away.
        if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(c.id);
      }}
      className="cursor-pointer transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:bg-primary-50 dark:hover:bg-gray-700/50 dark:focus-visible:bg-primary-900/20"
    >
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(c.id)}
          aria-label={`Select ${contactName(c)}`}
          className={checkboxCls}
        />
      </td>
      <td className="px-4 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <RowLink
            to={`/crm/contacts/${c.id}`}
            title={contactName(c)}
            className={`max-w-[16rem] truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-bold' : 'font-medium'}`}
          >
            {contactName(c)}
          </RowLink>
          {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
          {c.sms_opt_out && (
            <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">opted out</span>
          )}
          {c.archived_at && (
            <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">archived</span>
          )}
          {showSnoozeUi && isActiveSnooze(c.snoozed_until) && (
            <span className="flex-shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">snoozed</span>
          )}
        </div>
        {contactInfo && (
          <div className="mt-0.5 max-w-[16rem] truncate text-xs text-gray-500 dark:text-gray-400">{contactInfo}</div>
        )}
      </td>
      <td className="px-4 py-3">
        <StudyChips studies={c.studies} />
        {source && <div className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{source}</div>}
      </td>
      <td className="px-4 py-3">
        <LifecycleBadge status={c.lifecycle_status} />
        {c.milestone && (
          <div className="mt-1"><MilestoneChip milestone={c.milestone} /></div>
        )}
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <OwnerSelect
          contactId={c.id}
          value={c.owner_user_id}
          compact
          onChanged={(o) => onOwnerChanged(c.id, o)}
        />
        <OtherRecruitersNote c={c} className="mt-0.5" />
      </td>
      <td className="px-4 py-3">
        {c.last_comm ? (
          <>
            <div className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
              <ChannelIcon channel={c.last_comm.channel} />
              {relTime(c.last_comm.occurred_at)}
            </div>
            {c.last_comm.body && (
              <div className="mt-0.5 max-w-[16rem] truncate text-xs text-gray-400 dark:text-gray-500">{c.last_comm.body}</div>
            )}
          </>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        )}
      </td>
      <td
        className="whitespace-nowrap px-4 py-3 text-sm text-gray-500 dark:text-gray-400"
        title={arrivalOf(c) ? new Date(arrivalOf(c)!).toLocaleString() : undefined}
      >
        {dateTime(arrivalOf(c))}
      </td>
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-1.5">
          {showSnoozeUi && (
            <SnoozeButton
              snoozedUntil={c.snoozed_until}
              onSnooze={(iso) => onSnooze(c, iso)}
              label={contactName(c)}
            />
          )}
          <button
            onClick={() => onArchive(c, !c.archived_at)}
            aria-label={c.archived_at ? `Unarchive ${contactName(c)}` : `Archive ${contactName(c)}`}
            title={c.archived_at ? 'Unarchive' : 'Archive'}
            className="inline-flex h-11 w-11 items-center justify-center rounded-lg border border-gray-300 text-gray-500 transition-colors duration-150 hover:bg-gray-50 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200 sm:h-8 sm:w-8"
          >
            {c.archived_at ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
          </button>
        </div>
      </td>
    </tr>
  );
});

/**
 * The same lead as a phone card. Memoized for the same reason as ContactRow;
 * `ownerName` arrives pre-resolved so the card never has to hold the `team`
 * array (whose identity changes when the roster loads).
 */
const ContactCard = memo(function ContactCard({
  c, showSnoozeUi, ownerName, rowNav, onOpen,
}: {
  c: ContactListItem;
  showSnoozeUi: boolean;
  ownerName: string;
  rowNav: (to: string) => { onClick: (e: React.MouseEvent) => void; onAuxClick: (e: React.MouseEvent) => void };
  onOpen: (id: string) => void;
}) {
  const reply = needsReply(c.last_comm);
  const contactInfo = [c.phone_e164 ? formatPhone(c.phone_e164) : null, c.email].filter(Boolean).join(' · ');
  const source = sourceOf(c);
  return (
    <div
      role="button"
      tabIndex={0}
      {...rowNav(`/crm/contacts/${c.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && e.target === e.currentTarget) onOpen(c.id);
      }}
      className="cursor-pointer rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700/50"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-2">
          <RowLink
            to={`/crm/contacts/${c.id}`}
            title={contactName(c)}
            className={`min-w-0 truncate text-sm text-gray-900 dark:text-gray-100 ${reply ? 'font-bold' : 'font-medium'}`}
          >
            {contactName(c)}
          </RowLink>
          {reply && <span className="h-2 w-2 flex-shrink-0 rounded-full bg-primary-500" title="Needs your reply" />}
          {c.sms_opt_out && (
            <span className="flex-shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">opted out</span>
          )}
          {c.archived_at && (
            <span className="flex-shrink-0 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">archived</span>
          )}
          {showSnoozeUi && isActiveSnooze(c.snoozed_until) && (
            <span className="flex-shrink-0 rounded bg-primary-100 px-1.5 py-0.5 text-[10px] font-medium text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">snoozed</span>
          )}
        </span>
        <span className="flex flex-shrink-0 flex-col items-end gap-1">
          <LifecycleBadge status={c.lifecycle_status} />
          {c.milestone && <MilestoneChip milestone={c.milestone} />}
        </span>
      </div>
      {contactInfo && (
        <div className="mt-1 truncate text-xs text-gray-500 dark:text-gray-400">{contactInfo}</div>
      )}
      {(c.studies || []).length > 0 && (
        <div className="mt-2"><StudyChips studies={c.studies} /></div>
      )}
      {c.last_comm && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <ChannelIcon channel={c.last_comm.channel} />
          <span className="flex-shrink-0">{relTime(c.last_comm.occurred_at)}</span>
          {c.last_comm.body && <span className="min-w-0 truncate">{c.last_comm.body}</span>}
        </div>
      )}
      {/* Slim footer — owner as read-only text (assignment, snooze
          and archive live on the lead detail; the tap target here
          is the whole card). Unassigned stays amber so unrouted
          leads still stand out while scanning. */}
      <div className="mt-2 flex items-center justify-between gap-2 text-xs">
        <span
          className={`min-w-0 truncate ${
            c.owner_user_id
              ? 'text-gray-500 dark:text-gray-400'
              : 'font-medium text-amber-600 dark:text-amber-400'
          }`}
        >
          {c.owner_user_id ? ownerName : 'Unassigned'}
        </span>
        <span className="flex-shrink-0 text-gray-400 dark:text-gray-500">
          {source ? `${source} · ` : ''}{dateTime(arrivalOf(c))}
        </span>
      </div>
    </div>
  );
});

export default function Contacts({ mode = 'inbox' }: { mode?: 'inbox' | 'database' }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const rowNav = useRowNav();
  const { team } = useTeam();
  const isSuperAdmin = user?.role === 'super_admin';
  const isDatabase = mode === 'database';

  const [rows, setRows] = useState<ContactListItem[]>([]);
  const [stats, setStats] = useState<LifecycleStats | null>(null);
  const [studies, setStudies] = useState<StudyOption[]>([]);
  // Saved searches for the "List" filter (latest completed run per search).
  const [savedSearches, setSavedSearches] = useState<SavedSearch[]>([]);
  // Future-study interest lists for the "Interest list" filter.
  const [interestCats, setInterestCats] = useState<InterestCategory[]>([]);
  const [mergeCandidates, setMergeCandidates] = useState<MergeCandidate[]>([]);
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filters — one state object; filtersRef always holds the latest value so
  // runQuery never reads stale state when called right after a change. Seeded
  // from this mode's persisted set (apply() mirrors every change back), with a
  // fresh-mount URL seed (?list= from the Search run page) layered on top.
  const [filters, setFilters] = useState<Filters>(() => {
    const seed = consumeUrlFilterSeed();
    return seed ? { ...loadFilters(mode), ...seed } : loadFilters(mode);
  });
  const filtersRef = useRef<Filters>(filters);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Manual "New lead" create flow (item 9).
  const [showNewLead, setShowNewLead] = useState(false);
  const handleLeadCreated = (contactId: string) => {
    setShowNewLead(false);
    notifyStatsChanged(); // sidebar/tab counts refetch
    navigate(`/crm/contacts/${contactId}`);
  };

  // Selection / bulk actions
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);

  // Inbox view (list | board), persisted per browser. Database mode is always
  // list — and so are phones: the kanban board is a desktop tool, so below sm
  // the persisted 'board' pref is overridden (not rewritten) to the list.
  const isPhone = useIsPhone();
  const [view, setView] = useState<'list' | 'board'>(() =>
    getPref('inbox.view', 'list') === 'board' ? 'board' : 'list',
  );
  const isBoard = !isDatabase && view === 'board' && !isPhone;
  // Mirror for the stable mount/mode effects so they read the current view
  // without re-subscribing on every toggle.
  const isBoardRef = useRef(isBoard);
  isBoardRef.current = isBoard;
  // Mirror so the stable runQuery (deps []) reads the current mode: the inbox
  // hides archived leads, the database shows everyone.
  const isDatabaseRef = useRef(isDatabase);
  isDatabaseRef.current = isDatabase;
  // Bumped by the header Refresh button so the board refetches its columns too.
  const [boardRefresh, setBoardRefresh] = useState(0);

  // Super-admin test tools
  const [toolsOpen, setToolsOpen] = useState(false);
  const toolsRef = useRef<HTMLDivElement>(null);
  const [syncing, setSyncing] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [ticking, setTicking] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  // Mirrored for the row handlers (archive/snooze), which are useCallback'd with
  // stable identities so the memoized rows actually skip re-renders: they must
  // still see the CURRENT row count and hasMore, not the render they were born in.
  const rowCountRef = useRef(0);
  rowCountRef.current = rows.length;
  const hasMoreRef = useRef(false);
  hasMoreRef.current = hasMore;

  // Monotonic request id: a response only commits if it is still the newest
  // query, so a slow earlier fetch can never clobber a newer filter's results
  // and an in-flight Load-more can't append rows from a previous filter.
  const reqSeqRef = useRef(0);

  const runQuery = useCallback(async (offset: number, replace: boolean) => {
    const seq = ++reqSeqRef.current;
    if (replace) {
      setLoading(true);
      setError(null);
      setSelected(new Set());
    } else {
      setLoadingMore(true);
    }
    try {
      const res = await api.get('/api/crm/contacts', { params: buildParams(filtersRef.current, offset, !isDatabaseRef.current) });
      if (seq !== reqSeqRef.current) return; // stale response — a newer query owns the list
      const batch: ContactListItem[] = res.data?.contacts || [];
      setRows((prev) => {
        if (replace) return batch;
        // Dedup on append: a lead created between pages shifts the offset and
        // would repeat the page-boundary rows (duplicate React keys).
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...batch.filter((b) => !seen.has(b.id))];
      });
      setHasMore(batch.length === LIMIT);
      setTruncated(!!res.data?.truncated);
    } catch (e: any) {
      if (seq === reqSeqRef.current) setError(errMsg(e));
    } finally {
      if (seq === reqSeqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      // Match the list: the inbox list AND the board hide snoozed by default, so
      // their tab/column counts should too. Only the patient database and
      // "Show snoozed" request the full counts. (The conversation counts are
      // always snooze-filtered server-side, independent of this.)
      const showSnoozed =
        isDatabaseRef.current || filtersRef.current.showSnoozed ? 'true' : 'false';
      const params: Record<string, string> = { show_snoozed: showSnoozed };
      // The working pipeline views (inbox list default + board columns) hide
      // archived leads from their rows/cards, so their tab/column badges must
      // exclude archived too — otherwise "Screened (13)" sits over 2 visible
      // cards. The patient database always shows everyone, and the list's
      // archived-only "Show archived" view wants the full counts.
      if (!isDatabaseRef.current && !filtersRef.current.showArchived) params.hide_archived = 'true';
      const res = await api.get('/api/crm/contacts/stats', { params });
      setStats(res.data || null);
    } catch {
      // Tolerate silently — tabs render without counts.
    }
  }, []);

  // Re-pullable so Refresh and reconciliation (which flags new candidates) keep
  // the banner in sync, not just the initial mount.
  const loadMergeCandidates = useCallback(async () => {
    try {
      const res = await api.get('/api/crm/merge-candidates');
      setMergeCandidates(res.data?.merge_candidates || []);
    } catch {
      // Tolerate silently — the banner just won't show.
    }
  }, []);

  useEffect(() => {
    // Board view never renders the list, so skip its hidden 50-row fetch —
    // switching back to list always refetches (see switchView).
    if (isBoardRef.current) setLoading(false);
    else runQuery(0, true);
    loadStats();
    loadMergeCandidates();
    api.get('/api/crm/studies').then((r) => setStudies(r.data?.studies || [])).catch(() => {});
    // Saved searches with a completed run — the "List" filter's options.
    api.get('/api/crm/searches').then((r) => {
      const list: SavedSearch[] = (r.data?.searches || []).filter(
        (s: SavedSearch) => s.last_run && s.last_run.status === 'complete',
      );
      setSavedSearches(list);
    }).catch(() => {});
    // Future-study interest lists — the "Interest list" filter's options.
    api.get('/api/crm/interest-categories').then((r) => {
      setInterestCats(r.data?.categories || []);
    }).catch(() => {});
    const timerRef = debounceRef; // stable ref object — read .current at cleanup time
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [runQuery, loadStats, loadMergeCandidates]);

  // /crm (inbox) and /crm/database both render this component, so React keeps
  // the instance across the route switch — load the target mode's persisted
  // filters (each mode keeps its own), clear selection, and refetch.
  const modeRef = useRef(mode);
  useEffect(() => {
    if (modeRef.current === mode) return;
    modeRef.current = mode;
    // A deep link (Field-tag library → "Search patients") seeds filters over
    // this mode's persisted set; the seeded set is persisted like any apply().
    const seed = consumeUrlFilterSeed();
    const f = seed ? { ...loadFilters(mode), ...seed } : loadFilters(mode);
    if (seed) setPref(`${FILTERS_PREF}.${mode}`, JSON.stringify(f));
    filtersRef.current = f;
    setFilters(f);
    setSelected(new Set());
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!isBoardRef.current) runQuery(0, true);
  }, [mode, runQuery]);

  // Crossing down into a phone viewport while the persisted view is 'board':
  // the board never ran the list query, so the now-forced list needs page 0.
  const wasPhoneRef = useRef(isPhone);
  useEffect(() => {
    if (isPhone && !wasPhoneRef.current && !isDatabase && view === 'board') runQuery(0, true);
    wasPhoneRef.current = isPhone;
  }, [isPhone, isDatabase, view, runQuery]);

  /** Apply a filter change and re-query page 0 (text inputs debounce 300ms). */
  const apply = (patch: Partial<Filters>, debounce = false) => {
    const next = { ...filtersRef.current, ...patch };
    filtersRef.current = next;
    setFilters(next);
    setPref(`${FILTERS_PREF}.${mode}`, JSON.stringify(next)); // survive navigation
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Board view reads `filters` directly (KanbanBoard refetches its own
    // columns) — the hidden list query would be wasted there.
    if (isBoard) return;
    if (debounce) {
      debounceRef.current = setTimeout(() => runQuery(0, true), 300);
    } else {
      runQuery(0, true);
    }
  };

  // Quick search's "See all in Patients" (quicksearch/QuickSearchPalette.tsx) carries the typed
  // text in router state, never in the URL: seed the search filter with it once per arrival. Keyed
  // by the location, not the mount, because /crm/inbox and /crm/database share this instance and a
  // palette opened on this page lands here again. The state is then dropped so Back or a refresh
  // never re-seeds it over a later edit of the box.
  const quickSearchSeenRef = useRef<string | null>(null);
  useEffect(() => {
    const seed = (location.state as { quickSearch?: unknown } | null)?.quickSearch;
    if (typeof seed !== 'string' || !seed.trim() || quickSearchSeenRef.current === location.key) return;
    quickSearchSeenRef.current = location.key;
    apply({ q: seed.trim() });
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Reset the list AND the counts: clearing showSnoozed changes what the stats
  // request returns, and apply() only re-runs the list query.
  const clearFilters = () => { apply({ ...DEFAULT_FILTERS }); loadStats(); };
  const loadMore = () => runQuery(rows.length, false);
  const refresh = () => {
    if (!isBoard) runQuery(0, true); // board refetches via the token below
    loadStats();
    loadMergeCandidates(); // reconciliation (runSync) flags new candidates
    setBoardRefresh((n) => n + 1);
  };

  const switchView = (v: 'list' | 'board') => {
    if (v === view) return;
    setView(v);
    setPref('inbox.view', v);
    if (v === 'board') {
      setSelected(new Set()); // bulk selection is list-only
      if (debounceRef.current) clearTimeout(debounceRef.current); // pending list query is moot
    } else {
      // The list query is skipped while the board is shown, so filters may
      // have changed — always refetch page 0 on return to list.
      runQuery(0, true);
    }
  };

  const hasActiveFilters = !!(
    filters.q || filters.lifecycle.length || filters.eligibility.length || filters.milestone.length ||
    filters.study.length || filters.owner.length || filters.unassigned ||
    filters.showArchived || filters.showSnoozed || filters.list || filters.interest
  );

  // "Mine" is the exclusive shortcut for "just me" — active only when I'm the
  // sole selected owner (a broader multi-owner set is its own filter, not Mine).
  const mineActive = !!user?.id && filters.owner.length === 1 && filters.owner[0] === user.id;
  const toggleUnassigned = () => apply({ unassigned: !filters.unassigned, owner: [] });
  const toggleMine = () =>
    mineActive ? apply({ owner: [] }) : apply({ owner: user?.id ? [user.id] : [], unassigned: false });

  // Selection helpers
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleRow = useCallback((id: string) =>
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    }), []);
  const togglePage = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));

  // A bulk move to a negative disposition collects one reason for the whole
  // batch (the per-lead PATCH 400s without it).
  const [pendingBulkStatus, setPendingBulkStatus] = useState<string | null>(null);
  // A bulk move into a study-attributed positive (Screened / Randomized) needs
  // the "each lead keeps its own considering study" acknowledgement — the
  // per-lead PATCH 400s without study_confirmed (no single study can be right
  // for a whole batch).
  const [pendingBulkStudyStatus, setPendingBulkStudyStatus] = useState<string | null>(null);
  // The leads a bulk move could not touch because the randomized guard 409'd
  // them (multi-study lead lists, plan §6.4). ONE aggregate acknowledgement
  // covers the batch — asking N times, mid-batch, for a 200-lead move is not a
  // question anybody answers. The body is stashed verbatim so confirming
  // re-sends exactly the same move, to just these ids, acknowledged.
  const [bulkConflict, setBulkConflict] = useState<{
    ids: string[];
    body: BulkPatchBody;
    randomizedStudyTitle: string | null;
    randomizedAt: string | null;
  } | null>(null);

  /**
   * PATCH one batch sequentially. Genuine failures are counted (never abort the
   * batch); randomized-guard 409s are collected separately — they are not
   * failures, they are the question above. The first 409's payload is kept for
   * the modal's copy (a batch can be randomized into several studies; naming one
   * of them beats naming none).
   */
  const runBulkPatch = async (ids: string[], body: BulkPatchBody, acknowledged: boolean) => {
    setBulkProgress({ done: 0, total: ids.length });
    let failed = 0;
    const conflicts: string[] = [];
    let sample: { title: string | null; at: string | null } | null = null;
    for (let i = 0; i < ids.length; i++) {
      try {
        await api.patch(`/api/crm/contacts/${ids[i]}`, {
          ...body,
          ...(acknowledged ? { randomized_conflict_acknowledged: true } : {}),
        });
      } catch (e: any) {
        const data = e?.response?.data;
        // Acknowledged re-sends can't trip the guard again — a 409 there is a
        // real failure, so it must not loop back into the modal.
        if (!acknowledged && e?.response?.status === 409 && data?.reason === 'randomized_conflict') {
          conflicts.push(ids[i]);
          if (!sample) {
            sample = { title: data.randomized_study?.title ?? null, at: data.randomized_at ?? null };
          }
        } else {
          failed++;
        }
      }
      setBulkProgress({ done: i + 1, total: ids.length });
    }
    setBulkProgress(null);
    return { failed, conflicts, sample };
  };

  const bulkPatch = async (body: BulkPatchBody) => {
    const ids = Array.from(selected);
    if (ids.length === 0 || bulkBusy) return;
    setBulkBusy(true);
    setBulkError(null);
    const { failed, conflicts, sample } = await runBulkPatch(ids, body, false);
    setBulkBusy(false);
    if (failed > 0) setBulkError(`${failed} of ${ids.length} update${ids.length === 1 ? '' : 's'} failed. The rest were applied.`);
    // Guard-blocked leads stay exactly as they were until the recruiter answers.
    if (conflicts.length > 0) {
      setBulkConflict({
        ids: conflicts,
        body,
        randomizedStudyTitle: sample?.title ?? null,
        randomizedAt: sample?.at ?? null,
      });
    }
    setSelected(new Set());
    notifyStatsChanged(); // sidebar badges refetch immediately
    await Promise.all([runQuery(0, true), loadStats()]);
  };

  /** Re-send the moves the guard blocked, this time acknowledged. */
  const confirmBulkConflict = async () => {
    const pending = bulkConflict;
    setBulkConflict(null);
    if (!pending || bulkBusy) return;
    setBulkBusy(true);
    setBulkError(null);
    const { failed } = await runBulkPatch(pending.ids, pending.body, true);
    setBulkBusy(false);
    if (failed > 0) {
      setBulkError(`${failed} of ${pending.ids.length} update${pending.ids.length === 1 ? '' : 's'} failed. The rest were applied.`);
    }
    notifyStatsChanged();
    await Promise.all([runQuery(0, true), loadStats()]);
  };

  /** Declining leaves them unmoved — said plainly, the way this bar reports any
   *  partial result (the rest of the batch already went through). */
  const cancelBulkConflict = () => {
    const n = bulkConflict?.ids.length || 0;
    setBulkConflict(null);
    if (n > 0) {
      setBulkError(`${n} lead${n === 1 ? ' was' : 's were'} left unchanged — randomized into another study. The rest were applied.`);
    }
  };

  // Stats refresh after inline assignment is debounced: rapid triage would
  // otherwise re-fire the (fan-out) stats endpoint once per row.
  const statsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onOwnerChanged = useCallback((id: string, owner: string | null) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, owner_user_id: owner } : r)));
    if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
    statsDebounceRef.current = setTimeout(loadStats, 1500);
  }, [loadStats]);
  useEffect(() => () => { if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current); }, []);

  /**
   * Per-row quick archive/unarchive (mirrors Conversations.tsx toggleRowArchived,
   * simplified — the inbox has no conversation-tab counts to shuffle). In the
   * inbox the archived row leaves the working list (it requests
   * hide_archived=true), so remove it optimistically; in the database mode
   * archived rows stay visible, so flip the gray chip in place instead.
   */
  const archiveRow = useCallback(async (row: ContactListItem, archive: boolean) => {
    setError(null);
    const wasLen = rowCountRef.current;
    const isDatabase = isDatabaseRef.current;
    if (isDatabase) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id ? { ...r, archived_at: archive ? new Date().toISOString() : null } : r,
        ),
      );
    } else {
      setRows((prev) => prev.filter((r) => r.id !== row.id)); // optimistic remove
    }
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { archived: archive });
      notifyStatsChanged(); // sidebar badges
      if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
      statsDebounceRef.current = setTimeout(loadStats, 1500); // tab counts
      // Don't strand the recruiter on an empty page while more rows exist.
      if (!isDatabase && wasLen - 1 === 0 && hasMoreRef.current) runQuery(0, true);
    } catch (e: any) {
      runQuery(0, true); // simplest reliable revert: re-fetch page 0
      setError(errMsg(e));
    }
  }, [loadStats, runQuery]);

  /**
   * Per-row snooze: defer a lead to a future time (or un-snooze with null).
   * When hiding (a future time with "Show snoozed" off) the row leaves the
   * working list, mirroring archiveRow; otherwise snoozed_until updates in place
   * (so the icon + chip reflect it in the "Show snoozed" view).
   */
  const snoozeRow = useCallback(async (row: ContactListItem, untilIso: string | null) => {
    setError(null);
    const wasLen = rowCountRef.current;
    const hiding = isActiveSnooze(untilIso) && !filtersRef.current.showSnoozed;
    if (hiding) {
      setRows((prev) => prev.filter((r) => r.id !== row.id)); // leaves the working list
    } else {
      setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, snoozed_until: untilIso } : r)));
    }
    try {
      await api.patch(`/api/crm/contacts/${row.id}`, { snoozed_until: untilIso });
      notifyStatsChanged(); // sidebar badges
      if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
      statsDebounceRef.current = setTimeout(loadStats, 1500); // tab counts
      if (hiding && wasLen - 1 === 0 && hasMoreRef.current) runQuery(0, true);
    } catch (e: any) {
      runQuery(0, true); // simplest reliable revert
      setError(errMsg(e));
    }
  }, [loadStats, runQuery]);

  // Stable open-the-lead handler for the memoized rows/cards.
  const openContact = useCallback((id: string) => navigate(`/crm/contacts/${id}`), [navigate]);

  // List windowing (D-9). Both surfaces render the SAME `rows`, and both are
  // page-scrolled, so each gets its own slice: the table in <tr> pitch (no gap)
  // and the phone cards in card pitch (+12px from space-y-3). Inert below 100
  // rows, where the DOM stays exactly what it was.
  const tableSlice = useWindowedSlice({ count: rows.length, estimatedRowHeight: 73 });
  const cardSlice = useWindowedSlice({ count: rows.length, gap: 12, estimatedRowHeight: 140 });

  // Close the test-tools menu on outside click.
  useEffect(() => {
    if (!toolsOpen) return;
    const onDown = (e: MouseEvent) => {
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) setToolsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [toolsOpen]);

  const seedFlows = async () => {
    setSeeding(true); setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/internal/seed-default-flows');
      const flows = res.data?.flows || [];
      const created = flows.filter((f: any) => f.created).length;
      setSyncMsg(`Seeded automation flows: ${created} created, ${flows.length - created} already existed. Run reconciliation to enroll crm-test leads.`);
    } catch (e: any) {
      setSyncMsg(`Seed failed: ${errMsg(e)}`);
    } finally {
      setSeeding(false);
    }
  };

  const runSync = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/internal/run-reconciliation');
      const d = res.data || {};
      setSyncMsg(`Synced ${d.total ?? 0} lead(s): ${d.created ?? 0} created, ${d.linked ?? 0} linked, ${d.flagged ?? 0} flagged, ${d.enrolled ?? 0} enrolled${d.errors ? `, ${d.errors} error(s)` : ''}.`);
      refresh();
    } catch (e: any) {
      setSyncMsg(`Sync failed: ${errMsg(e)}`);
    } finally {
      setSyncing(false);
    }
  };

  const runTick = async () => {
    setTicking(true); setSyncMsg(null);
    try {
      const res = await api.post('/api/crm/internal/run-automation-tick');
      const d = res.data || {};
      setSyncMsg(`Automation tick: claimed ${d.claimed ?? 0}, advanced ${d.advanced ?? 0}, SMS enqueued ${d.enqueuedSms ?? 0}, completed ${d.completed ?? 0}${d.errors ? `, ${d.errors} error(s)` : ''}.`);
    } catch (e: any) {
      setSyncMsg(`Tick failed: ${errMsg(e)}`);
    } finally {
      setTicking(false);
    }
  };

  const anyToolBusy = seeding || syncing || ticking;

  const inputCls =
    'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-400';
  const menuItemCls =
    'flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-700 dark:focus:bg-gray-700';
  const thCls = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

  const tabs: Array<{ value: string; label: string; count: number | null }> = [
    { value: '', label: 'All', count: stats ? stats.total : null },
    ...LIFECYCLES.map((l) => ({
      value: l.value,
      label: l.label,
      count: stats ? (stats.by_status?.[l.value] ?? 0) : null,
    })),
  ];

  // ---- Unified filter system (shared <FilterBar>) ----
  const studyLabel = (slug: string) => {
    const s = studies.find((x) => x.slug === slug);
    return s ? studyInternalLabel(s) : prettySlug(slug);
  };

  /**
   * The board's single-study scope, resolved from slug to a real crm.studies row
   * (multi-study lead lists, plan §6.4). The filter carries SLUGS and the board
   * needs a study id to move a membership, so the resolution has to happen here
   * where `studies` lives. Null unless exactly one filtered slug maps to a CRM
   * study: no scope or several is an ambiguous drag, and a lead-only slug has no
   * study row (and therefore no membership) to move at all.
   */
  const boardScopedStudy = useMemo(() => {
    if (filters.study.length !== 1) return null;
    const s = studies.find((x) => x.slug === filters.study[0] && x.source === 'crm' && x.id);
    return s?.id ? { id: s.id, title: studyInternalLabel(s) } : null;
  }, [filters.study, studies]);
  const ownerLabel = useCallback((id: string) => team.find((m) => m.id === id)?.name || 'Owner', [team]);

  // Secondary filters, revealed by the "Add filter" popover.
  const moreFilters: FilterDef[] = [
    {
      type: 'multiselect',
      key: 'study',
      label: 'Study',
      values: filters.study,
      onChange: (v) => apply({ study: v }),
      options: [
        { value: '', label: 'Any study' },
        ...studies.map((s) => ({ value: s.slug, label: `${studyInternalLabel(s)} (${s.slug}) — ${s.count}` })),
      ],
    },
    {
      // Owner is a multi-select PLUS an "unassigned" checkbox — the two combine
      // (owner IN (...) OR unassigned), so a custom node holds both. In the inbox
      // the Unassigned quick chip mirrors this checkbox; the patient database has
      // only this one, so it must live here to stay filterable in both modes.
      type: 'custom',
      key: 'owner',
      label: 'Owner',
      node: (
        <div className="space-y-1.5">
          <FilterMultiSelect
            values={filters.owner}
            onChange={(v) => apply({ owner: v })}
            options={[{ value: '', label: 'Any owner' }, ...team.map((m) => ({ value: m.id, label: m.name }))]}
            label="Owner"
          />
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={filters.unassigned}
              onChange={(e) => apply({ unassigned: e.target.checked })}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Include unassigned
          </label>
        </div>
      ),
    },
    {
      type: 'multiselect',
      key: 'eligibility',
      label: 'Incoming eligibility',
      values: filters.eligibility,
      onChange: (v) => apply({ eligibility: v }),
      title: "Best landing-form verdict across the lead's studies; with a study filter on, the verdict of that study's own form. Manually-added contacts (no form submission) aren't matched.",
      options: [{ value: '', label: 'Any eligibility' }, ...INCOMING_ELIGIBILITY_OPTIONS],
    },
    {
      type: 'multiselect',
      key: 'milestone',
      label: 'Milestone',
      values: filters.milestone,
      onChange: (v) => apply({ milestone: v }),
      title: "The pertinent-milestone sub-line shown under each lead's status.",
      options: [
        { value: '', label: 'Any milestone' },
        ...MILESTONE_FILTER_OPTIONS.map((k) => ({ value: k as string, label: MILESTONE_LABELS[k] })),
      ],
    },
    {
      // The Search tab's board bridge: scope the list/board to a saved search's
      // latest run (a frozen member list). Complex medical/answers cohorts are
      // BUILT in /crm/search now — this filter is how they get WORKED here.
      type: 'custom',
      key: 'list',
      label: 'List (from Search)',
      node: (
        <div>
          <FilterSelect
            value={filters.list}
            onChange={(runId) => {
              const s = savedSearches.find((x) => x.last_run?.id === runId);
              apply({ list: runId, listName: s ? s.name : runId ? filters.listName || 'Search list' : '' });
            }}
            label="Search list"
            options={[
              { value: '', label: 'Any (no list)' },
              // Preserve a deep-linked run that isn't a saved search's latest.
              ...(filters.list && !savedSearches.some((s) => s.last_run?.id === filters.list)
                ? [{ value: filters.list, label: filters.listName || 'Linked list' }]
                : []),
              ...savedSearches.map((s) => ({
                value: s.last_run!.id,
                label: `${s.name} — ${s.last_run!.final_total?.toLocaleString() ?? '?'} leads (${relTime(s.last_run!.finished_at)})`,
              })),
            ]}
            className="w-full"
          />
          <p className="mt-1 text-xs text-gray-400">
            Narrows to a search run's snapshot list. Build searches (conditions, meds, labs, form answers…) in the{' '}
            <button type="button" onClick={() => navigate('/crm/search')} className="font-medium text-primary-600 hover:underline dark:text-primary-400">
              Search tab
            </button>.
          </p>
        </div>
      ),
    },
    {
      // Future-study interest lists (owner 2026-08-20): leads parked as "right
      // profile for a study type we regularly get". When a study in that
      // indication opens, this filter IS the call list.
      type: 'custom',
      key: 'interest',
      label: 'Future-study list',
      node: (
        <div>
          <FilterSelect
            value={filters.interest}
            onChange={(id) => {
              const c = interestCats.find((x) => x.id === id || x.key === id);
              apply({ interest: id, interestName: c ? c.label : id ? filters.interestName || id : '' });
            }}
            label="Future-study list"
            options={[
              { value: '', label: 'Any (no list)' },
              // Preserve a deep-linked key/id the catalog fetch didn't match.
              ...(filters.interest && !interestCats.some((c) => c.id === filters.interest || c.key === filters.interest)
                ? [{ value: filters.interest, label: filters.interestName || filters.interest }]
                : []),
              ...interestCats.map((c) => ({
                value: c.id,
                label: c.is_active ? c.label : `${c.label} (retired)`,
              })),
            ]}
            className="w-full"
          />
          <p className="mt-1 text-xs text-gray-400">
            Leads kept for a standard study type we regularly get — add them from a
            lead's Profile tab or the close-out dialog.
          </p>
        </div>
      ),
    },
  ];

  // Applied-filter summary chips — one per selected value so each is removable.
  // The visible quick chips already cover Mine / Unassigned / Show archived /
  // Show snoozed, so those are omitted here (except Unassigned in the patient
  // database, which has no quick chip for it).
  const activeChips: ActiveChip[] = [];
  filters.study.forEach((slug) =>
    activeChips.push({ key: `study-${slug}`, label: `Study: ${studyLabel(slug)}`, onRemove: () => apply({ study: filters.study.filter((s) => s !== slug) }) }),
  );
  // Owner: one chip per owner, except the sole-current-user case (that's "Mine").
  filters.owner.forEach((id) => {
    if (mineActive && id === user?.id) return;
    activeChips.push({ key: `owner-${id}`, label: `Owner: ${ownerLabel(id)}`, onRemove: () => apply({ owner: filters.owner.filter((o) => o !== id) }) });
  });
  if (filters.unassigned && isDatabase) {
    activeChips.push({ key: 'unassigned', label: 'Unassigned', onRemove: () => apply({ unassigned: false }) });
  }
  filters.eligibility.forEach((v) => {
    const el = INCOMING_ELIGIBILITY_OPTIONS.find((o) => o.value === v);
    activeChips.push({ key: `eligibility-${v}`, label: `Eligibility: ${el?.label || v}`, onRemove: () => apply({ eligibility: filters.eligibility.filter((x) => x !== v) }) });
  });
  filters.milestone.forEach((v) =>
    activeChips.push({
      key: `milestone-${v}`,
      label: `Milestone: ${MILESTONE_LABELS[v as keyof typeof MILESTONE_LABELS] || v}`,
      onRemove: () => apply({ milestone: filters.milestone.filter((x) => x !== v) }),
    }),
  );
  if (filters.list) {
    activeChips.push({
      key: 'list',
      label: `List: ${filters.listName || 'Search list'}`,
      onRemove: () => apply({ list: '', listName: '' }),
    });
  }
  if (filters.interest) {
    activeChips.push({
      key: 'interest',
      label: `Future study: ${filters.interestName || filters.interest}`,
      onRemove: () => apply({ interest: '', interestName: '' }),
    });
  }

  // The "Add filter" badge mirrors exactly the secondary filters surfaced as
  // chips below — quick toggles (Mine / Unassigned) are intentionally not counted.
  const moreFiltersCount = activeChips.length;

  return (
    <div className="space-y-4">
      <PageHeader
        icon={isDatabase ? Users : Inbox}
        title={isDatabase ? 'Patient database' : 'Leads'}
        count={stats ? stats.total : null}
        subtitle={isDatabase ? 'Everyone who has ever come through — search by name, phone, or email.' : undefined}
        actions={
          <>
            <button
              onClick={() => setShowNewLead(true)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0"
            >
              <UserPlus className="h-4 w-4" />
              <span className="hidden sm:inline">New lead</span>
            </button>
            {/* Board view is a desktop tool — phones always get the list, so
                the toggle (and the super-admin test tools below) stay off the
                phone header. */}
            {!isDatabase && (
              <span className="hidden sm:inline-flex">
                <SegmentedControl
                  label="Inbox view"
                  value={view}
                  onChange={(v) => switchView(v as 'list' | 'board')}
                  options={[
                    { value: 'list', label: 'List', icon: <List className="h-4 w-4" /> },
                    { value: 'board', label: 'Board', icon: <LayoutGrid className="h-4 w-4" /> },
                  ]}
                />
              </span>
            )}
            <RefreshButton onClick={refresh} loading={loading} label="Refresh leads" />
            {isSuperAdmin && !isDatabase && (
              <div ref={toolsRef} className="relative hidden sm:block">
                <button
                  onClick={() => setToolsOpen((s) => !s)}
                  aria-expanded={toolsOpen}
                  aria-haspopup="menu"
                  className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
                >
                  {anyToolBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                  Test tools
                  <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${toolsOpen ? 'rotate-180' : ''}`} />
                </button>
                {toolsOpen && (
                  <div
                    role="menu"
                    className={`absolute right-0 ${Z.menu} mt-2 w-60 rounded-lg border border-gray-200 bg-white p-1 shadow-lg dark:border-gray-700 dark:bg-gray-800`}
                  >
                    <button role="menuitem" onClick={() => { setToolsOpen(false); seedFlows(); }} disabled={anyToolBusy} className={menuItemCls}>
                      {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Workflow className="h-4 w-4" />}
                      {seeding ? 'Seeding…' : 'Seed flows'}
                    </button>
                    <button role="menuitem" onClick={() => { setToolsOpen(false); runSync(); }} disabled={anyToolBusy} className={menuItemCls}>
                      {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      {syncing ? 'Syncing…' : 'Run reconciliation'}
                    </button>
                    <button role="menuitem" onClick={() => { setToolsOpen(false); runTick(); }} disabled={anyToolBusy} className={menuItemCls}>
                      {ticking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      {ticking ? 'Running…' : 'Run automation tick'}
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        }
      />

      {syncMsg && (
        <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {syncMsg}
        </div>
      )}

      {/* The recruiter's own follow-ups (renders nothing when none are open) */}
      {!isDatabase && <MyTasksPanel />}

      {/* Pipeline tabs (inbox list view only — board columns replace them).
          Multi-select: toggle several stages; "All" clears back to no filter. */}
      {!isDatabase && !isBoard && (
        <SegmentedTabs
          label="Lead pipeline"
          multiple
          tabs={tabs}
          values={filters.lifecycle}
          onChange={(v) => apply({ lifecycle: v })}
        />
      )}

      {/* Toolbar: search + quick filters + Add-filter popover + active-filter
          chips. One swipeable strip on phones (mobileScroll) so the lead list
          starts right under the search box. */}
      <FilterBar mobileScroll active={<ActiveFilters chips={activeChips} onClearAll={hasActiveFilters ? clearFilters : undefined} />}>
        <SearchInput
          value={filters.q}
          onChange={(v) => apply({ q: v }, true)}
          placeholder="Search name, phone, or email…"
          label="Search leads"
          className="flex-1 basis-56 max-sm:shrink-0"
        />
        {!isDatabase && (
          <FilterChip active={filters.unassigned} onClick={toggleUnassigned}>
            Unassigned{stats && stats.unassigned > 0 ? ` (${stats.unassigned.toLocaleString()})` : ''}
          </FilterChip>
        )}
        {!!user?.id && <FilterChip active={mineActive} onClick={toggleMine}>Mine</FilterChip>}
        {/* Inbox only (list + board). On the list this flips to the archived-only
            set (conversation=archived); on the board it adds archived cards into
            the columns. Both refresh stats so the badges track the visible rows. */}
        {!isDatabase && (
          <FilterChip
            active={filters.showArchived}
            onClick={() => { apply({ showArchived: !filters.showArchived }); loadStats(); }}
            icon={<Archive className="h-4 w-4" />}
          >
            Show archived
          </FilterChip>
        )}
        {/* Inbox only (list + board) — both are working views that hide snoozed
            leads by default, so both need the escape hatch. */}
        {!isDatabase && (
          <FilterChip
            active={filters.showSnoozed}
            onClick={() => { apply({ showSnoozed: !filters.showSnoozed }); loadStats(); }}
            icon={<MoonStar className="h-4 w-4" />}
          >
            Show snoozed
          </FilterChip>
        )}
        <FilterSelect value={filters.sort} onChange={(v) => apply({ sort: v })} auto label="Sort order">
          <option value="">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="activity">Recent activity</option>
          <option value="eligibility">By eligibility</option>
        </FilterSelect>
        <AddFilterMenu filters={moreFilters} activeCount={moreFiltersCount} />
      </FilterBar>

      {mergeCandidates.length > 0 && (
        <button
          type="button"
          onClick={() => setShowMergeModal(true)}
          className="flex w-full items-center gap-2 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-left text-sm text-yellow-800 transition-colors duration-150 hover:bg-yellow-100 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400 dark:hover:bg-yellow-900/30"
        >
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">
            {mergeCandidates.length} pending merge candidate{mergeCandidates.length === 1 ? '' : 's'} (phone/email pointed to different contacts — flagged for review, not auto-merged).
          </span>
          <span className="flex-shrink-0 font-medium underline">Review</span>
        </button>
      )}

      {showNewLead && (
        <NewLeadModal
          studies={studies}
          team={team}
          onClose={() => setShowNewLead(false)}
          onCreated={handleLeadCreated}
        />
      )}

      {showMergeModal && (
        <MergeCandidatesModal
          candidates={mergeCandidates}
          onClose={() => setShowMergeModal(false)}
          onResolved={(id) => {
            // Optimistic removal for instant feedback; auto-close when the queue
            // empties. A server reconcile follows because one merge can also sweep
            // sibling candidates that referenced the same loser.
            setMergeCandidates((prev) => {
              const next = prev.filter((c) => c.id !== id);
              if (next.length === 0) setShowMergeModal(false);
              return next;
            });
            loadMergeCandidates();
            // A merge moves children onto the kept contact — refresh the list/stats.
            runQuery(0, true);
            loadStats();
          }}
        />
      )}

      {!isBoard && truncated && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-800 dark:border-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
          This filter matched more contacts than the current cap — showing a partial set. Narrow the filter to see all matches.
        </div>
      )}

      {!isBoard && error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}

      {/* Bulk actions (list view only — board has no selection) */}
      {!isBoard && (selected.size > 0 || bulkBusy) && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-primary-200 bg-primary-50 p-3 dark:border-primary-800 dark:bg-primary-900/20">
          <span className="text-sm font-medium text-primary-800 dark:text-primary-300">{selected.size} selected</span>
          <select
            value=""
            disabled={bulkBusy}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              bulkPatch({ owner_user_id: v === UNASSIGNED ? null : v });
            }}
            aria-label="Assign selected leads to"
            className={`${inputCls} w-auto cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <option value="">Assign to…</option>
            <option value={UNASSIGNED}>Unassigned</option>
            {team.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <select
            value=""
            disabled={bulkBusy}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              // Negative dispositions need a reason — collect one for the batch.
              if (FINAL_DISPOSITIONS.includes(v)) { setPendingBulkStatus(v); return; }
              // Study-attributed positives need the per-lead-study acknowledgement.
              if (STUDY_ATTRIBUTED_STAGES.includes(v)) { setPendingBulkStudyStatus(v); return; }
              bulkPatch({ lifecycle_status: v });
            }}
            aria-label="Set status for selected leads"
            className={`${inputCls} w-auto cursor-pointer disabled:cursor-not-allowed disabled:opacity-50`}
          >
            <option value="">Set status…</option>
            {LIFECYCLES.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
          {pendingBulkStatus && (
            <DispositionReasonModal
              status={pendingBulkStatus}
              askStudy={STUDY_ATTRIBUTED_STAGES.includes(pendingBulkStatus)}
              bulkCount={selected.size}
              onCancel={() => setPendingBulkStatus(null)}
              onConfirm={(reasonCode, note) => {
                const status = pendingBulkStatus;
                setPendingBulkStatus(null);
                const body: { lifecycle_status: string; reason_code: string; disposition_note?: string; study_confirmed?: boolean } = {
                  lifecycle_status: status, reason_code: reasonCode,
                };
                if (note) body.disposition_note = note;
                // Bulk can't pick one study for a batch — each lead keeps its
                // own considering study (acknowledged in the modal).
                if (STUDY_ATTRIBUTED_STAGES.includes(status)) body.study_confirmed = true;
                void bulkPatch(body);
              }}
            />
          )}
          {pendingBulkStudyStatus && (
            <StageStudyConfirmModal
              status={pendingBulkStudyStatus}
              bulkCount={selected.size}
              onCancel={() => setPendingBulkStudyStatus(null)}
              onConfirm={() => {
                const status = pendingBulkStudyStatus;
                setPendingBulkStudyStatus(null);
                void bulkPatch({ lifecycle_status: status, study_confirmed: true });
              }}
            />
          )}
          {bulkBusy && bulkProgress && (
            <span className="inline-flex items-center gap-1.5 text-sm text-primary-700 dark:text-primary-300">
              <Loader2 className="h-4 w-4 animate-spin" />
              Updating {bulkProgress.done}/{bulkProgress.total}
            </span>
          )}
          {!bulkBusy && (
            <button
              onClick={() => setSelected(new Set())}
              className="ml-auto inline-flex items-center gap-1 rounded-lg px-2 py-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-900/40"
            >
              <X className="h-4 w-4" /> Clear
            </button>
          )}
        </div>
      )}

      {!isBoard && bulkError && (
        <Alert tone="error" icon={false}>{bulkError}</Alert>
      )}

      {/* The batch's randomized-guard acknowledgement. OUTSIDE the bulk toolbar
          on purpose: the batch clears the selection when it finishes, which
          unmounts that whole bar — the question has to outlive it. */}
      {bulkConflict && (
        <RandomizedConflictModal
          bulkCount={bulkConflict.ids.length}
          randomizedStudyTitle={bulkConflict.randomizedStudyTitle}
          randomizedAt={bulkConflict.randomizedAt}
          onCancel={cancelBulkConflict}
          onConfirm={() => { void confirmBulkConflict(); }}
        />
      )}

      {/* Results */}
      {isBoard ? (
        <KanbanBoard
          filters={filters}
          stats={stats}
          refreshToken={boardRefresh}
          scopedStudy={boardScopedStudy}
          onMoved={() => {
            // Same debounce as inline owner changes: rapid drags shouldn't
            // re-fire the fan-out stats endpoint once per card.
            if (statsDebounceRef.current) clearTimeout(statsDebounceRef.current);
            statsDebounceRef.current = setTimeout(loadStats, 1500);
          }}
        />
      ) : loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-5 w-5 animate-spin text-primary-600" /></div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          {isDatabase ? (
            <Users className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          ) : (
            <Inbox className="mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
          )}
          <h3 className="mb-1 text-lg font-medium text-gray-900 dark:text-gray-100">
            {isDatabase ? 'No contacts match' : 'No leads match'}
          </h3>
          <p className="max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {hasActiveFilters
              ? 'Try clearing or broadening your filters.'
              : isDatabase
                ? 'Patients will appear here as leads come in.'
                : 'New leads will appear here as they come in.'}
          </p>
          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <X className="h-4 w-4" /> Clear filters
            </button>
          ) : (
            <button
              onClick={() => setShowNewLead(true)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              <UserPlus className="h-4 w-4" /> New lead
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-hidden rounded-xl border border-gray-200 shadow-sm dark:border-gray-700 sm:block">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th className="w-10 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        ref={(el) => { if (el) el.indeterminate = selected.size > 0 && !allSelected; }}
                        onChange={togglePage}
                        aria-label="Select all leads on this page"
                        className={checkboxCls}
                      />
                    </th>
                    {['Lead', 'Study', 'Status', 'Owner', 'Last activity', 'Arrival'].map((h) => (
                      <th key={h} className={thCls}>{h}</th>
                    ))}
                    <th className="w-10 px-4 py-3" aria-label="Actions" />
                  </tr>
                </thead>
                <tbody ref={tableSlice.containerRef} className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-gray-800">
                  {/* Windowed above 100 rows (D-9). The table itself is
                      untouched — <thead>, columns and every <tr> are what they
                      were; only the count of rendered <tr>s changes, with two
                      spacer rows holding the scrollbar honest. borderTopWidth:0
                      keeps the tbody's divide-y from drawing a rule across a
                      spacer. Below 100 rows nothing here runs at all. */}
                  {tableSlice.topPad > 0 && (
                    <tr aria-hidden="true" data-spacer="1" style={{ borderTopWidth: 0 }}>
                      <td colSpan={8} style={{ height: tableSlice.topPad, padding: 0, borderTopWidth: 0 }} />
                    </tr>
                  )}
                  {rows.slice(tableSlice.start, tableSlice.end).map((c) => (
                    <ContactRow
                      key={c.id}
                      c={c}
                      isSelected={selected.has(c.id)}
                      isDatabase={isDatabase}
                      showSnoozeUi={!isDatabase && !filters.showArchived}
                      rowNav={rowNav}
                      onOpen={openContact}
                      onToggleSelect={toggleRow}
                      onOwnerChanged={onOwnerChanged}
                      onArchive={archiveRow}
                      onSnooze={snoozeRow}
                    />
                  ))}
                  {tableSlice.bottomPad > 0 && (
                    <tr aria-hidden="true" data-spacer="1" style={{ borderTopWidth: 0 }}>
                      <td colSpan={8} style={{ height: tableSlice.bottomPad, padding: 0, borderTopWidth: 0 }} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile cards */}
          <div ref={cardSlice.containerRef} className="space-y-3 sm:hidden">
            {/* Same windowing as the table (the phone list renders the same
                `rows`), in card PITCH: card height + the stack's 12px gap. */}
            {cardSlice.topPad > 0 && (
              <div aria-hidden="true" data-spacer="1" style={{ height: cardSlice.topPad }} />
            )}
            {rows.slice(cardSlice.start, cardSlice.end).map((c) => (
              <ContactCard
                key={c.id}
                c={c}
                showSnoozeUi={!isDatabase && !filters.showArchived}
                ownerName={c.owner_user_id ? ownerLabel(c.owner_user_id) : ''}
                rowNav={rowNav}
                onOpen={openContact}
              />
            ))}
            {cardSlice.bottomPad > 0 && (
              <div aria-hidden="true" data-spacer="1" style={{ height: cardSlice.bottomPad }} />
            )}
          </div>

          {hasMore && (
            <div className="flex justify-center">
              <button
                onClick={loadMore}
                disabled={loadingMore}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0"
              >
                {loadingMore ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
